// ================================
// MEMBER SIGNUP FORM
// Shared by admin's "Add Member Manually" and the staff Add Member screen.
// Everyone doing this is on their own personal phone — no dedicated
// USB/Bluetooth scanner hardware — so this opens the phone's actual
// camera and decodes the barcode on the back of the ID directly (every
// U.S. license encodes its data as a PDF417 barcode in the same AAMVA
// field-code format). The decoded text is sent to the server to parse,
// and the name/DOB/address fields below get filled in for review. DOB is
// always required either way — scanned or typed by hand — since age
// verification isn't optional for either signup path.
//
// This uses @zxing/browser directly against our own <video> element
// rather than html5-qrcode (used for the QR scans elsewhere in the app).
// html5-qrcode decodes from a canvas it sizes to the *on-screen* preview's
// CSS pixel width — fine for a QR code, but a PDF417 license barcode packs
// far denser data and needs real resolution to resolve; cropped down to a
// few hundred CSS pixels, it was never decodable and the scanner just sat
// there forever. @zxing/browser instead draws from the video element's
// native camera resolution (videoWidth/videoHeight), so it reads from the
// actual high-res frame the camera captured.
// ================================
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BrowserPDF417Reader } from '@zxing/browser';

export default function MemberSignupForm({ onSubmit, submitting, submitLabel = 'Add Member' }) {
  const [form, setForm] = useState({ name: '', phone: '', address: '', zip: '', dob: '' });
  const [showScanner, setShowScanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState(''); // '', 'ok', 'failed'
  const [verificationMethod, setVerificationMethod] = useState('manual');
  const videoRef = useRef(null);
  const controlsRef = useRef(null);

  const upd = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const fldStyle = { width: '100%', padding: '10px 12px', background: '#0a0f20', border: '1px solid #0d1e3a', borderRadius: 9, color: '#e8e8e8', fontSize: 13, outline: 'none', fontFamily: 'DM Sans,sans-serif' };
  const lblStyle = { fontSize: 10, color: '#3a5a8a', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 5 };

  // Start/stop the camera scanner while the panel is open.
  useEffect(() => {
    if (!showScanner) return;
    const reader = new BrowserPDF417Reader();
    let stopped = false;

    reader.decodeFromConstraints(
      // Ask for a high-res feed explicitly — the browser's own default
      // camera resolution is often lower than a PDF417 barcode needs to
      // resolve reliably.
      { video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } },
      videoRef.current,
      (result, err, controls) => {
        controlsRef.current = controls; // populated on every frame attempt, so cleanup can always reach it
        if (result && !stopped) {
          stopped = true;
          controls.stop();
          setShowScanner(false);
          handleScan(result.getText());
        }
        // err here is just "no barcode found in this frame" on every miss — ignore it.
      }
    ).catch(() => {
      if (!stopped) alert('Could not access the camera. Check camera permissions and try again.');
    });

    return () => {
      stopped = true;
      if (controlsRef.current) { controlsRef.current.stop(); controlsRef.current = null; }
    };
  }, [showScanner]);

  async function handleScan(raw) {
    setScanning(true);
    setScanStatus('');
    try {
      const r = await axios.post('/api/members/parse-id-scan', { raw });
      if (r.data.parsed) {
        setForm(p => ({
          ...p,
          name: r.data.fullName || p.name,
          address: r.data.address ? `${r.data.address}${r.data.city ? ', ' + r.data.city : ''}` : p.address,
          zip: r.data.zip || p.zip,
          dob: r.data.dob
        }));
        setVerificationMethod('barcode');
        setScanStatus('ok');
      } else {
        setScanStatus('failed');
      }
    } catch (e) {
      setScanStatus('failed');
    } finally {
      setScanning(false);
    }
  }

  function handleManualDobChange(v) {
    upd('dob', v);
    setVerificationMethod('manual'); // typing over a scanned DOB means it's no longer scan-verified
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.dob) { alert('Date of birth is required — scan an ID or enter it manually.'); return; }
    onSubmit({ ...form, verificationMethod });
  }

  const age = form.dob ? ageFromDob(form.dob) : null;
  const underage = age !== null && age < 21;

  return (
    <form onSubmit={handleSubmit}>
      {/* Barcode scan */}
      <div style={{ marginBottom: 14, border: '1px dashed #1e3a6a', borderRadius: 10, padding: 14 }}>
        <label style={lblStyle}>Scan ID Barcode (back of license — fills in the fields below)</label>
        {!showScanner ? (
          <button type="button" onClick={() => { setShowScanner(true); setScanStatus(''); }} disabled={scanning}
            style={{ width: '100%', background: '#1e3a8a', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 9, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif' }}>
            {scanning ? 'Reading...' : '📷 Scan ID Barcode'}
          </button>
        ) : (
          <div>
            <div style={{ position: 'relative', width: '100%', borderRadius: 9, overflow: 'hidden', background: '#000' }}>
              <video ref={videoRef} muted playsInline style={{ width: '100%', display: 'block' }} />
              {/* Purely a visual aiming guide — a PDF417 barcode is a wide, short strip, not square like a QR code. Decoding reads the full frame, not just this box. */}
              <div style={{ position: 'absolute', left: '8%', right: '8%', top: '38%', bottom: '38%', border: '2px solid #4a9eff', borderRadius: 6, pointerEvents: 'none' }} />
            </div>
            <p style={{ fontSize: 11, color: '#3a5a8a', marginTop: 8 }}>Hold the back of the ID (the barcode side) steady in frame.</p>
            <button type="button" onClick={() => setShowScanner(false)}
              style={{ marginTop: 6, background: 'none', border: 'none', color: '#3a5a8a', fontSize: 12, cursor: 'pointer' }}>
              Cancel scan
            </button>
          </div>
        )}
        {scanStatus === 'ok' && <p style={{ fontSize: 11, color: '#1a9e5a', marginTop: 8 }}>✓ Scanned — fields below filled in, review before submitting.</p>}
        {scanStatus === 'failed' && <p style={{ fontSize: 11, color: '#c0101a', marginTop: 8 }}>Couldn't read a date of birth from that scan — try again, or enter details manually below.</p>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lblStyle}>Full Name</label><input style={fldStyle} type="text" placeholder="Full name" value={form.name} onChange={e => upd('name', e.target.value)} required /></div>
        <div><label style={lblStyle}>Phone Number</label><input style={fldStyle} type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => upd('phone', e.target.value)} required /></div>
        <div><label style={lblStyle}>Delivery Address</label><input style={fldStyle} type="text" placeholder="Street address" value={form.address} onChange={e => upd('address', e.target.value)} /></div>
        <div><label style={lblStyle}>Zip Code</label><input style={fldStyle} type="text" placeholder="00000" value={form.zip} onChange={e => upd('zip', e.target.value)} /></div>
        <div>
          <label style={lblStyle}>Date of Birth {verificationMethod === 'barcode' && <span style={{ color: '#1a9e5a' }}>(from scan)</span>}</label>
          <input style={fldStyle} type="date" value={form.dob} onChange={e => handleManualDobChange(e.target.value)} required />
        </div>
      </div>

      {underage && <p style={{ fontSize: 12, color: '#c0101a', marginBottom: 12 }}>⚠ This date of birth is under 21 — cannot be added.</p>}

      <p style={{ fontSize: 11, color: '#3a5a8a', marginBottom: 12 }}>A blank card will be pulled from inventory and assigned to this member — hand them the card, they set their own PIN when they activate it.</p>

      <button type="submit" disabled={submitting || underage} style={{ background: '#1e3a8a', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontFamily: 'DM Sans,sans-serif', opacity: (submitting || underage) ? 0.6 : 1 }}>
        {submitting ? 'Adding...' : submitLabel}
      </button>
    </form>
  );
}

function ageFromDob(dob) {
  const [y, m, d] = dob.split('-').map(Number);
  if (!y || !m || !d) return null;
  const parsed = new Date(y, m - 1, d);
  const now = new Date();
  let age = now.getFullYear() - parsed.getFullYear();
  const hadBirthday = (now.getMonth() > parsed.getMonth()) || (now.getMonth() === parsed.getMonth() && now.getDate() >= parsed.getDate());
  if (!hadBirthday) age--;
  return age;
}
